import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IDog, IDogRun } from "@eagerdog/interfaces";

import { Modal, useModal } from "src/components/Modal/Modal";
import Icon from "src/components/Icon/Icon";
import DogForm from "src/components/StepForm/DogForm/DogForm";
import { toast } from "src/components/Toast/ToastManager";
import RunResultQuickAction, { ISportOption } from "../RunResultQuickAction/RunResultQuickAction";
import RunShareComposer from "../RunShareComposer/RunShareComposer";
import ManualRunEntryModal from "src/routes/DogProfile/ManualRunEntry/ManualRunEntryModal";

import styles from "./GetStartedCard.module.scss";

interface IProps {
    /** Called after the run is shared to the feed so the feed can refresh. */
    onPosted?: () => void;
}

const GetStartedCard: React.FC<IProps> = ({ onPosted }) => {
    const navigate = useNavigate();
    const dogModal = useModal();
    const runPickerModal = useModal();
    const [runEntryTarget, setRunEntryTarget] = useState<{ dog: IDog; sport: ISportOption } | null>(null);
    const [shareTarget, setShareTarget] = useState<{ dog: IDog; run: Partial<IDogRun> } | null>(null);
    // ManualRunEntryModal calls onSuccess then onClose in the same tick; see QuickActions.
    const sharePending = useRef(false);

    const closeRunFlow = () => {
        runPickerModal.isShown && runPickerModal.toggle();
        setRunEntryTarget(null);
        if (sharePending.current) {
            sharePending.current = false;
        }
    };

    const shortcuts: { label: string; icon: string; primary?: boolean; onClick: () => void }[] = [
        { label: "Enter an event", icon: "events", primary: true, onClick: () => navigate("/events") },
        { label: "Find courses", icon: "school", onClick: () => navigate("/courses") },
        { label: "My entries", icon: "page", onClick: () => navigate("/account?t=1") },
        { label: "Add a dog", icon: "paw", onClick: dogModal.toggle },
        { label: "Log a run", icon: "trophy", onClick: () => { setRunEntryTarget(null); runPickerModal.toggle(); } },
        { label: "Find a club", icon: "group", onClick: () => navigate("/kennel-clubs") },
    ];

    return (
        <div className={styles.GetStartedCard}>
            <div className={styles.eyebrow}>Shortcuts</div>
            <div className={styles.title}>Get started</div>

            <div className={styles.grid}>
                {shortcuts.map((s) => (
                    <button
                        key={s.label}
                        type="button"
                        className={`${styles.tile} ${s.primary ? styles.primary : ""}`}
                        onClick={s.onClick}
                    >
                        <span className={styles.iconBox}>
                            <Icon type={s.icon} className={styles.icon} />
                        </span>
                        <span className={styles.label}>{s.label}</span>
                    </button>
                ))}
            </div>

            <Modal
                isShown={dogModal.isShown}
                hide={dogModal.toggle}
                className="dogForm"
                modalContent={
                    <DogForm
                        onFinish={(dog?: IDog) => {
                            dogModal.toggle();
                            if (dog) {
                                toast.show({
                                    title: "Add a Dog",
                                    content: `${dog.call_name} successfully added to your account!`,
                                    duration: 10000,
                                    type: "success"
                                });
                            }
                        }}
                    />
                }
            />

            <Modal
                isShown={runPickerModal.isShown && !runEntryTarget}
                hide={closeRunFlow}
                className="runResultPickerModal"
                modalContent={
                    <RunResultQuickAction
                        onClose={closeRunFlow}
                        onContinue={(dog, sport) => setRunEntryTarget({ dog, sport })}
                    />
                }
            />

            {runPickerModal.isShown && runEntryTarget && (
                <ManualRunEntryModal
                    isOpen={true}
                    onClose={closeRunFlow}
                    dog={runEntryTarget.dog}
                    sportType={runEntryTarget.sport.value}
                    sanctioningClub={runEntryTarget.sport.sanctioningClub}
                    onSuccess={(createdRun?: any) => {
                        const target = runEntryTarget;
                        if (createdRun?._id && target) {
                            sharePending.current = true;
                            setShareTarget({ dog: target.dog, run: createdRun });
                        }
                    }}
                />
            )}

            <Modal
                isShown={!!shareTarget}
                hide={() => setShareTarget(null)}
                className="momentComposerModal"
                modalContent={
                    shareTarget ? (
                        <RunShareComposer
                            dog={shareTarget.dog}
                            run={shareTarget.run}
                            onClose={() => setShareTarget(null)}
                            onPosted={() => onPosted?.()}
                        />
                    ) : <></>
                }
            />
        </div>
    );
};

export default GetStartedCard;
